import { Component, EventEmitter, Input, OnInit, Output, OnChanges, SimpleChanges, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { VendedorService } from './services/vendedor.service';
import { Vendedor } from './interfaces/vendedor.interface';
import { MessageService } from 'primeng/api';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { AbcEmpresasCustomizablesComponent } from '@p-parametria/mantenimiento/abc-empresas/components/abc-empresas-customizables/abc-empresas-customizables.component';

@Component({
  selector: 'app-vendedor-form',
  templateUrl: './vendedor-form.component.html'
})
export class VendedorFormComponent implements OnInit, OnChanges, OnDestroy {
  @Input() model: Vendedor | null = null;
  @Output() onSave = new EventEmitter<{ success: boolean; message: string }>();
  @Output() onCustomizables = new EventEmitter<void>();

  form: FormGroup;
  loading: boolean = false;
  tiposDropdown: any[] = [];
  estadosDropdown: any[] = [];
  usuariosDropdown: any[] = [];
  private destroy$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private vendedorService: VendedorService,
    private messageService: MessageService,
    private dialog: MatDialog
  ) {
    this.initForm();
  }

  ngOnInit(): void {
    this.loadTiposVendedor();
    this.loadEstados();
    this.loadUsuariosDropdown();
    if (this.model) {
      this.form.patchValue(this.model);
    }
  }

  initForm(): void {
    this.form = this.fb.group({
      vendedor: [null, Validators.required],
      tipo: [null],
      exterior: [''],
      oficinaTramite: [''],
      regional: [''],
      estado: [null],
      telefono: [0],
      nombre: [{ value: '', disabled: true }, Validators.required],
      cedula: [''],
      interior: [''],
      cargo: [''],
      ciudad: [''],
      cp: [0]
    });

    // Suscribirse a cambios en el campo vendedor
    this.form.get('vendedor')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(value => {
        if (value) {
          this.onVendedorSelected(value);
        }
      });
  }

  onVendedorSelected(susuario: string): void {
    const selectedUser = this.usuariosDropdown.find(user => user.susuario === susuario);
    if (selectedUser) {
      this.form.patchValue({
        nombre: selectedUser.nombreusuario.trim()
      });
    }
  }

  loadUsuariosDropdown(): void {
    const payload = { tope: '9999', usuario: '' };
    this.vendedorService.getUsuariosDropdown(payload).subscribe({
      next: (response) => {
        if (response.success) {
          this.usuariosDropdown = response.data;
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'No se pudieron cargar los usuarios'
          });
        }
      },
      error: (error) => {
        console.error('Error al cargar usuarios para el dropdown:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Error al cargar los usuarios'
        });
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['model'] && changes['model'].currentValue) {
      const vendedor = changes['model'].currentValue;
      this.form.patchValue({
        vendedor: vendedor.vendedor,
        tipo: vendedor.tipo,
        nombre: vendedor.nombre,
        cedula: vendedor.calle,
        exterior: vendedor.numero_ext,
        interior: vendedor.numero_int,
        oficinaTramite: vendedor.cruzamiento,
        cargo: vendedor.colonia,
        ciudad: vendedor.ciudad,
        regional: vendedor.del_muni,
        estado: vendedor.cve_estado,
        cp: vendedor.cp,
        telefono: vendedor.telefono
      });
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadTiposVendedor(): void {
    this.vendedorService.getTiposVendedor().subscribe({
      next: (response) => {
        if (response.success) {
          this.tiposDropdown = response.data.map((item: any) => ({
            label: item.sOpcion,
            value: item.sValor
          }));
        }
      },
      error: (error) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Error al cargar tipos de vendedor'
        });
      }
    });
  }

  loadEstados(): void {
    this.vendedorService.getEstados().subscribe({
      next: (response) => {
        if (response.success) {
          this.estadosDropdown = response.data.map((item: any) => ({
            label: item.sOpcion,
            value: item.sOpcion
          }));
        }
      },
      error: (error) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Error al cargar estados'
        });
      }
    });
  }

  create(): void {
    if (this.form.valid) {
      this.loading = true;
      const formData = this.form.getRawValue();
      
      // Asegurar que los campos numéricos sean números
      formData.telefono = Number(formData.telefono) || 0;
      formData.cp = Number(formData.cp) || 0;
      
      this.vendedorService.createVendedor(formData)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (response) => {
            if (response.success) {
              this.onSave.emit({
                success: true,
                message: 'Vendedor creado correctamente'
              });
            } else {
              this.messageService.add({
                severity: 'error',
                summary: 'Error',
                detail: 'Error al crear el vendedor'
              });
            }
          },
          error: (error) => {
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: 'Error al crear el vendedor'
            });
          },
          complete: () => {
            this.loading = false;
          }
        });
    } else {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Por favor complete todos los campos requeridos'
      });
    }
  }

  update(): void {
    if (this.form.invalid) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Por favor complete todos los campos requeridos'
      });
      return;
    }

    this.loading = true;
    const formData = this.form.getRawValue();
    
    // Asegurar que los campos numéricos sean números
    formData.telefono = Number(formData.telefono) || 0;
    formData.cp = Number(formData.cp) || 0;
    
    // Mapeo correcto de los campos del formulario a los campos del API
    const payload = {
      que: 2,
      tope: "",
      cmd: "",
      cve_ven: formData.vendedor || "",
      tipo: formData.tipo || "",
      nombre: formData.nombre || "",
      calle: formData.cedula || "",
      numero_ext: formData.exterior || "",
      numero_int: formData.interior || "",
      cruzamiento: formData.oficinaTramite || "",
      colonia: formData.cargo || "",
      ciudad: formData.ciudad || "",
      del_muni: formData.regional || "",
      cve_estado: formData.estado || "",
      cp: formData.cp,
      telefono: formData.telefono,
      plazo: 0,
      precio: 0,
      zona: "",
      productoPrincipal: "",
      tipoVendedor: "",
      acumulado: 0
    };

    this.vendedorService.updateVendedor(payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          if (response.success) {
            this.onSave.emit({
              success: true,
              message: 'Vendedor actualizado correctamente'
            });
          } else {
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: 'Error al actualizar el vendedor'
            });
          }
          this.loading = false;
        },
        error: (error) => {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Error al actualizar el vendedor'
          });
          this.loading = false;
        }
      });
  }

  openCustomizables(): void {
    this.dialog.open(AbcEmpresasCustomizablesComponent, {
      width: '1000px',
      height: '1000px',
      disableClose: true,
      panelClass: ['custom-dialog-zindex'],
      hasBackdrop: true,
      autoFocus: false,
      data: {
        sTabla: 'vendedores',
        sClave: `${this.form.get('vendedor')?.value}`,
        sNombre: this.form.get('nombre')?.value,
        sTipo: 'Opcion'
      }
    });
  }
} 